import {Component, Inject, ChangeDetectionStrategy} from '@angular/core';
import {FormGroup, ReactiveFormsModule} from "@angular/forms";
import {
  WaButtonDirective,
  WaDialogDirective,
  WaDividerDirective,
  WaScrollerDirective,
  WaToastService
} from "angular-awesome";
import {DIALOG_DATA, DialogRef} from "@angular/cdk/dialog";
import {Survey} from "../../../api/models/survey";
import {StaffMember} from "../../../api/models/staff-member";
import {Exam} from "../../../api/models/exam";
import {ExamSupervisionAssignmentsData} from "../../../api/models/exam-assignments";
import {
  SupervisionFixedStaffMemberComponent
} from "./supervision-fixed-staff-member/supervision-fixed-staff-member.component";
import {SupervisionStaffMemberComponent} from "./supervision-staff-member/supervision-staff-member.component";
import {
  SupervisionExcludedStaffMemberComponent
} from "./supervision-excluded-staff-member/supervision-excluded-staff-member.component";


@Component({
  selector: 'app-exam-assignments-supervision-dialog',
  imports: [ReactiveFormsModule, WaDialogDirective, WaButtonDirective, WaDividerDirective, SupervisionFixedStaffMemberComponent, SupervisionStaffMemberComponent, WaScrollerDirective, SupervisionExcludedStaffMemberComponent],
  templateUrl: './exam-assignments-supervision-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './exam-assignments-supervision-dialog.component.scss',
})
export class ExamAssignmentsSupervisionDialogComponent {
  examAssignmentsForm = new FormGroup({});

  //private isLoading: boolean = true;
  //private lockAcquired: boolean = false;

  survey: Survey | null = null;
  exam: Exam | null = null;
  staffMembers: StaffMember[] = [];
  fixedSupervisors: StaffMember[] = [];
  suggestedSupervisors: StaffMember[] = [];
  excludedSupervisors: StaffMember[] = [];


  constructor(private dialogRef: DialogRef<ExamSupervisionAssignmentsData>, private toastService: WaToastService, @Inject(DIALOG_DATA) data: ExamSupervisionAssignmentsData) {
    if (!data) {
      return;
    }
    if (!data?.survey) {
      return;
    }
    if (!data?.exam) {
      return;
    }
    this.survey = data.survey;
    this.exam = data.exam;
    //use clones of input arrays
    this.staffMembers = [...data.staffMembers].filter(this.canSupervise).sort(this.compareStaffMembers);    this.fixedSupervisors = [...data.fixedSupervisors].sort(this.compareStaffMembers);
    this.suggestedSupervisors = [...data.suggestedSupervisors].sort(this.compareStaffMembers);
    this.excludedSupervisors = [...data.excludedSupervisors].sort(this.compareStaffMembers);
    // exclude elements that are already listed in fixedSupervisors, suggestedSupervisors or excludedSupervisors
    const excludedIds = new Set<number>([
      ...this.fixedSupervisors.map(s => s.id),
      ...this.suggestedSupervisors.map(s => s.id),
      ...this.excludedSupervisors.map(s => s.id)
    ]);
    this.staffMembers = this.staffMembers.filter(s => !excludedIds.has(s.id));
  }

  compareStaffMembers(a: StaffMember, b: StaffMember) {
    if (a.last_name === b.last_name) {
      return a.first_name.localeCompare(b.first_name);
    } else {
      return a.last_name.localeCompare(b.last_name);
    }
  }

  isValid(): boolean {
    return (!!this.survey && !!this.exam);
  }

  apply(): void {
    this.dialogRef.close({
      survey: this.survey,
      exam: this.exam,
      staffMembers: this.staffMembers,
      fixedSupervisors: this.fixedSupervisors,
      suggestedSupervisors: this.suggestedSupervisors,
      excludedSupervisors: this.excludedSupervisors
    });
  }

  cancel(): void {
    this.dialogRef.close();
  }

  sortSupervisorLists() {
    this.staffMembers.sort(this.compareStaffMembers);
    this.fixedSupervisors.sort(this.compareStaffMembers);
    this.suggestedSupervisors.sort(this.compareStaffMembers);
    this.excludedSupervisors.sort(this.compareStaffMembers);
  }

  setFixedStaffMember(staffmember: StaffMember): void {
    this.fixedSupervisors.push(staffmember);
    this.suggestedSupervisors = this.suggestedSupervisors.filter(s => s.id !== staffmember.id);
    this.staffMembers = this.staffMembers.filter(s => s.id !== staffmember.id);
    this.excludedSupervisors = this.excludedSupervisors.filter(s => s.id !== staffmember.id);
    this.sortSupervisorLists();
  }

  setRegularStaffMember(staffmember: StaffMember): void {
    this.staffMembers.push(staffmember);
    this.fixedSupervisors = this.fixedSupervisors.filter(s => s.id !== staffmember.id);
    this.suggestedSupervisors = this.suggestedSupervisors.filter(s => s.id !== staffmember.id);
    this.excludedSupervisors = this.excludedSupervisors.filter(s => s.id !== staffmember.id);
    this.sortSupervisorLists();
  }

  setSuggestedStaffMember(staffmember: StaffMember): void {
    this.suggestedSupervisors.push(staffmember);
    this.suggestedSupervisors.sort(this.compareStaffMembers);
    this.fixedSupervisors = this.fixedSupervisors.filter(s => s.id !== staffmember.id);
    this.staffMembers = this.staffMembers.filter(s => s.id !== staffmember.id);
    this.excludedSupervisors = this.excludedSupervisors.filter(s => s.id !== staffmember.id);
    this.sortSupervisorLists();
  }

  setExcludedStaffMember(staffmember: StaffMember): void {
    this.excludedSupervisors.push(staffmember);
    this.excludedSupervisors.sort(this.compareStaffMembers);
    this.fixedSupervisors = this.fixedSupervisors.filter(s => s.id !== staffmember.id);
    this.suggestedSupervisors = this.suggestedSupervisors.filter(s => s.id !== staffmember.id);
    this.staffMembers = this.staffMembers.filter(s => s.id !== staffmember.id);
    this.sortSupervisorLists();

  }

  // return true if required supervisions is a positive number (exclude profs who do not supervise)
  canSupervise(staffmember: StaffMember): boolean {
    return staffmember.required_supervisions > 0;
  }
}


